/**
 * Never offer one of OUR companies as the customer.
 *
 * ---- Why this is required, not defensive ----
 *
 * An enquiry mail routinely carries the CHANDLER's letterhead rather than the buyer's — the
 * customer fills in our form, or a salesman forwards on our paper. `resolveCustomerHint`
 * (`api/useMailExtraction.ts`) strips punctuation and `LIKE`s the first few words, and our own
 * company is in the `Customer` master as well as `Company`:
 *
 *     Company : "AQUA MARIS SHIPS SUPPLY -(S.P.S - L.L.C)"
 *     Customer: "AQUA MARIS SHIPS SUPPLY SPS LLC"     ← same company, different punctuation
 *
 * So the hint resolves to exactly ONE candidate, which any UI would reasonably present as a
 * confident guess — one keypress from an enquiry billed to ourselves.
 * `useMailExtraction.ts:259-261` already warns about this, which is why the resolver returns
 * candidates and never picks one. This module is what lets a caller act on that warning.
 *
 * ---- Why normalised, not exact ----
 *
 * The two masters spell the same company differently. `===` on the pair above returns `false`, so
 * an exact guard would silently never fire — the worst kind, because it looks present.
 *
 * ---- Why it fails OPEN ----
 *
 * An empty own-company list means the master hasn't loaded, not that we have no companies.
 * Filtering everything out then would block every create. Better to let a candidate through and
 * rely on the confirmation bar showing it, than to make the feature unusable when a read is slow.
 */

/** Case-folded, stripped of everything that is not a letter or digit. */
export function normaliseCompanyName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '')
}

export function isOwnCompany(candidate: string, ownCompanies: string[]): boolean {
  const key = normaliseCompanyName(candidate)
  if (key === '') return false
  return ownCompanies.some((own) => normaliseCompanyName(own) === key)
}

/** Candidates that are NOT one of ours, in their original order. See the header on failing open. */
export function filterOutOwnCompanies(candidates: string[], ownCompanies: string[]): string[] {
  if (ownCompanies.length === 0) return candidates
  return candidates.filter((c) => !isOwnCompany(c, ownCompanies))
}
